import { useState, type ElementType, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { cn } from '@/lib/utils';
import { BillingAddressPlaceholderCard } from '../billing-address-placeholder-card/BillingAddressPlaceholderCard';

export interface BillingSectionProps {
  icon: ElementType;
  iconClassName: string;
  iconBgClassName: string;
  title: string;
  subtitle?: string;
  addressCount: number;
  canAdd: boolean;
  onAdd: () => void;
  children?: ReactNode;
  /** Message shown when canAdd is false and there are no addresses */
  emptyMessage?: ReactNode;
}

export function BillingSection({
  icon: Icon,
  iconClassName,
  iconBgClassName,
  title,
  subtitle,
  addressCount,
  canAdd,
  onAdd,
  children,
  emptyMessage,
}: BillingSectionProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((v) => !v)}
        className="flex items-center gap-3 w-full px-5 py-4 border-b border-border text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <div
          className={cn(
            'flex size-9 items-center justify-center rounded-xl shrink-0',
            iconBgClassName
          )}
        >
          <Icon size={16} className={iconClassName} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{title}</span>
            <span className="text-[11px] font-semibold bg-muted text-muted-foreground rounded px-1.5 py-0.5">
              {addressCount}
            </span>
          </div>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {collapsed ? (
          <ChevronRight size={16} className="text-muted-foreground shrink-0" aria-hidden />
        ) : (
          <ChevronDown size={16} className="text-muted-foreground shrink-0" aria-hidden />
        )}
      </button>

      {!collapsed && (
        <div className="p-5">
          {!canAdd && addressCount === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-10 text-center">
              <MapPin size={24} className="mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-muted-foreground text-sm">
                {emptyMessage ?? <Trans>No billing addresses.</Trans>}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {children}
              {canAdd && <BillingAddressPlaceholderCard onAdd={onAdd} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
